/**
 * Lock & Distribute Flow — The decisive moment.
 * Full-screen takeover. Review → Slide-to-Lock → Processing → Success.
 * Feels like finalizing reality, not completing a workflow.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useFIE } from "@/data/fie/context";
import { ArrowLeft, Lock, ShieldCheck, CheckCircle2, Download, Share2, Eye } from "lucide-react";
import { cn } from "@/lib/utils";

function fmt(value: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value);
}

type FlowStep = "review" | "processing" | "success";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function LockDistributeFlow({ open, onClose }: Props) {
  const {
    visibleForecast: f, visibleDistributions, selectedPeriodId,
    isPeriodLocked, snapshotAssumptions, addAuditEntry,
    canViewEconomics, periods,
  } = useFIE();

  const [step, setStep] = useState<FlowStep>("review");
  const [processingIndex, setProcessingIndex] = useState(0);

  const locked = isPeriodLocked(selectedPeriodId);
  const period = periods.find(p => p.id === selectedPeriodId);
  const periodLabel = period?.label || selectedPeriodId;

  useEffect(() => {
    if (open) {
      setStep("review");
      setProcessingIndex(0);
    }
  }, [open]);

  const handleLock = useCallback(() => {
    setStep("processing");
    setProcessingIndex(0);

    const steps = [0, 1, 2, 3];
    steps.forEach((i) => {
      setTimeout(() => {
        setProcessingIndex(i + 1);
        if (i === steps.length - 1) {
          const snapId = snapshotAssumptions(selectedPeriodId, "manual");
          addAuditEntry({
            id: `AUD-${Date.now()}-lock`,
            timestamp: new Date().toISOString(),
            userId: "current-user",
            userName: "Current User",
            userRole: "owner",
            action: "period_locked",
            objectType: "period",
            objectId: selectedPeriodId,
            objectLabel: `Period ${selectedPeriodId} locked and distributions generated`,
            oldValue: "active",
            newValue: "closed",
            reason: "Lock & Distribute executed",
            effectiveDate: new Date().toISOString().split("T")[0],
            periodId: selectedPeriodId,
            snapshotId: snapId,
          });
          setTimeout(() => setStep("success"), 400);
        }
      }, 450 * (i + 1));
    });
  }, [selectedPeriodId, snapshotAssumptions, addAuditEntry]);

  if (!open) return null;

  const revenue = f.revenue?.expected || 0;
  const costs = f.totalCosts?.expected || 0;
  const profit = f.netProfit?.expected || 0;
  const totalDistributed = visibleDistributions.reduce((s, d) => s + d.projectedPayout.expected, 0);
  const unresolvedCount = f.revenueLines
    ? f.revenueLines.reduce((s, r) => s + (r.lineage?.unresolvedItems?.length || 0), 0)
    : 0;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col animate-fade-in">
      {/* Minimal top bar */}
      <div className="flex items-center justify-between px-8 h-14 shrink-0">
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {step === "success" ? "Back to Dashboard" : "Cancel"}
        </button>
        <p className="text-[11px] font-medium text-muted-foreground/50 tracking-[0.15em] uppercase">
          {periodLabel}
        </p>
      </div>

      {/* Content — centered, generous whitespace */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-xl mx-auto px-8 py-8">
          {step === "review" && (
            <ReviewStep
              revenue={revenue}
              costs={costs}
              profit={profit}
              distributions={visibleDistributions.map(d => ({
                name: d.partnerName,
                payout: d.projectedPayout.expected,
                pct: d.ownershipPercentage,
              }))}
              totalDistributed={totalDistributed}
              confidence={f.confidence.score}
              completeness={f.completeness.overall}
              unresolvedCount={unresolvedCount}
              canViewEconomics={canViewEconomics}
              locked={locked}
              onLock={handleLock}
            />
          )}
          {step === "processing" && (
            <ProcessingStep currentIndex={processingIndex} />
          )}
          {step === "success" && (
            <SuccessStep
              periodLabel={periodLabel}
              profit={profit}
              totalDistributed={totalDistributed}
              snapshotId={`SNAP-${selectedPeriodId}`}
              fingerprint={f.proof.fingerprint}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Review Step — Single continuous flow ──
function ReviewStep({
  revenue, costs, profit, distributions, totalDistributed,
  confidence, completeness, unresolvedCount, canViewEconomics, locked,
  onLock,
}: {
  revenue: number; costs: number; profit: number;
  distributions: { name: string; payout: number; pct: number }[];
  totalDistributed: number;
  confidence: number; completeness: number; unresolvedCount: number;
  canViewEconomics: boolean; locked: boolean;
  onLock: () => void;
}) {
  return (
    <div className="animate-in slide-in-from-bottom-4 duration-500 ease-out space-y-14">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-[34px] font-bold tracking-tight leading-tight">
          Review & Finalize
        </h1>
        <p className="text-[15px] text-muted-foreground mt-3 max-w-md mx-auto">
          You're about to lock this period. These numbers will become final.
        </p>
      </div>

      {/* Primary Number — the hero */}
      {canViewEconomics && (
        <div className="text-center space-y-5">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground mb-2">
              Net Profit
            </p>
            <p className="text-[52px] font-bold tracking-tight leading-none tabular-nums">
              {fmt(profit)}
            </p>
          </div>

          {/* Revenue / Costs — subtle, underneath */}
          <div className="flex items-center justify-center gap-8 text-muted-foreground">
            <span className="text-[14px] tabular-nums">
              <span className="text-muted-foreground/60 mr-1.5">Revenue</span>
              <span className="font-medium text-foreground">{fmt(revenue)}</span>
            </span>
            <span className="text-border">·</span>
            <span className="text-[14px] tabular-nums">
              <span className="text-muted-foreground/60 mr-1.5">Costs</span>
              <span className="font-medium text-foreground">{fmt(costs)}</span>
            </span>
          </div>
        </div>
      )}

      {/* Distributions */}
      {distributions.length > 0 && canViewEconomics && (
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground mb-5">
            Distributions
          </p>
          <div className="space-y-0">
            {distributions.map(d => (
              <div key={d.name} className="flex items-baseline justify-between py-3 border-b border-border/15 last:border-0">
                <div>
                  <p className="text-[14px] font-medium">{d.name}</p>
                  <p className="text-[11px] text-muted-foreground/60">{d.pct}%</p>
                </div>
                <p className="text-[15px] font-semibold tabular-nums">{fmt(d.payout)}</p>
              </div>
            ))}
          </div>
          <div className="flex items-baseline justify-between pt-4 mt-1">
            <p className="text-[12px] text-muted-foreground">Total</p>
            <p className="text-[14px] font-semibold tabular-nums">{fmt(totalDistributed)}</p>
          </div>
        </div>
      )}

      {/* Health — inline, calm */}
      <div className="flex items-center justify-center gap-8">
        <HealthDot label="Confidence" value={confidence} />
        <HealthDot label="Completeness" value={completeness} suffix="%" />
      </div>

      {/* Things to be aware of */}
      {unresolvedCount > 0 && (
        <div className="text-center">
          <p className="text-[12px] font-medium text-muted-foreground mb-2">
            Things to be aware of
          </p>
          <p className="text-[13px] text-muted-foreground/70">
            {unresolvedCount} item{unresolvedCount > 1 ? "s" : ""} with missing data or assumptions.
            Locking will finalize figures as-is.
          </p>
        </div>
      )}

      {/* Slide to Lock */}
      <div className="pt-4">
        {locked ? (
          <p className="text-center text-[13px] text-muted-foreground flex items-center justify-center gap-1.5">
            <Lock className="h-3.5 w-3.5" /> This period is already locked
          </p>
        ) : (
          <SlideToLock onComplete={onLock} />
        )}
      </div>
    </div>
  );
}

// ── Slide to Lock ──
function SlideToLock({ onComplete }: { onComplete: () => void }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState(0);
  const [completed, setCompleted] = useState(false);
  const thumbWidth = 56;

  const getMaxOffset = () => {
    if (!trackRef.current) return 200;
    return trackRef.current.offsetWidth - thumbWidth;
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (completed) return;
    setDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragging || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - thumbWidth / 2;
    const max = getMaxOffset();
    setOffset(Math.max(0, Math.min(x, max)));
  };

  const handlePointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    const max = getMaxOffset();
    if (offset >= max * 0.85) {
      setOffset(max);
      setCompleted(true);
      setTimeout(onComplete, 300);
    } else {
      setOffset(0);
    }
  };

  const progress = getMaxOffset() > 0 ? offset / getMaxOffset() : 0;

  return (
    <div className="space-y-3">
      <div
        ref={trackRef}
        className="relative h-14 rounded-2xl bg-muted/60 overflow-hidden select-none touch-none"
      >
        {/* Progress fill */}
        <div
          className="absolute inset-y-0 left-0 bg-foreground/5 rounded-2xl transition-all"
          style={{ width: `${(progress * 100).toFixed(1)}%`, transition: dragging ? "none" : "width 0.3s ease-out" }}
        />

        {/* Label */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <p className={cn(
            "text-[13px] font-medium tracking-wide transition-opacity duration-300",
            progress > 0.3 ? "opacity-0" : "text-muted-foreground"
          )}>
            Slide to Lock & Finalize
          </p>
        </div>

        {/* Thumb */}
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className={cn(
            "absolute top-1 bottom-1 w-12 rounded-xl flex items-center justify-center cursor-grab active:cursor-grabbing",
            completed
              ? "bg-foreground text-background"
              : "bg-foreground text-background shadow-sm"
          )}
          style={{
            left: `${offset + 4}px`,
            transition: dragging ? "none" : "left 0.3s ease-out",
          }}
        >
          {completed ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <Lock className="h-4 w-4" />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Processing Step ──
const PROCESSING_STEPS = [
  "Finalizing financials",
  "Locking snapshot",
  "Generating distributions",
  "Creating audit record",
];

function ProcessingStep({ currentIndex }: { currentIndex: number }) {
  return (
    <div className="animate-fade-in flex flex-col items-center justify-center min-h-[60vh] space-y-12">
      <div className="h-20 w-20 rounded-full border-2 border-foreground/10 flex items-center justify-center">
        <Lock className={cn(
          "h-8 w-8 transition-all duration-700",
          currentIndex >= PROCESSING_STEPS.length ? "text-foreground" : "text-muted-foreground/40 animate-pulse"
        )} />
      </div>

      <div className="space-y-4">
        {PROCESSING_STEPS.map((label, i) => (
          <div
            key={label}
            className={cn(
              "flex items-center gap-3 transition-all duration-500",
              i < currentIndex ? "opacity-100" : i === currentIndex ? "opacity-40" : "opacity-10"
            )}
          >
            {i < currentIndex ? (
              <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
            ) : (
              <div className={cn(
                "h-4 w-4 rounded-full border shrink-0",
                i === currentIndex ? "border-foreground/30 animate-pulse" : "border-muted-foreground/15"
              )} />
            )}
            <span className="text-[14px] text-foreground">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Success Step ──
function SuccessStep({
  periodLabel, profit, totalDistributed, snapshotId, fingerprint, onClose,
}: {
  periodLabel: string; profit: number; totalDistributed: number;
  snapshotId: string; fingerprint: string; onClose: () => void;
}) {
  return (
    <div className="animate-in slide-in-from-bottom-6 duration-700 ease-out flex flex-col items-center text-center min-h-[60vh] justify-center space-y-12">
      {/* Icon */}
      <div className="h-24 w-24 rounded-full bg-success/8 flex items-center justify-center">
        <ShieldCheck className="h-12 w-12 text-success" />
      </div>

      {/* Title */}
      <div className="space-y-3">
        <h1 className="text-[32px] font-bold tracking-tight">{periodLabel} Closed</h1>
        <p className="text-[15px] text-muted-foreground max-w-sm mx-auto">
          Financials are now final and distributions are locked.
        </p>
      </div>

      {/* Numbers */}
      <div className="flex items-start gap-16">
        <div className="text-left">
          <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-1">Net Profit</p>
          <p className="text-[28px] font-bold tabular-nums tracking-tight">{fmt(profit)}</p>
        </div>
        <div className="text-left">
          <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-1">Total Distributed</p>
          <p className="text-[28px] font-bold tabular-nums tracking-tight">{fmt(totalDistributed)}</p>
        </div>
      </div>

      {/* Proof */}
      <div className="space-y-3">
        <div className="flex items-center gap-6 justify-center">
          <div>
            <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-0.5">Snapshot</p>
            <p className="text-[13px] font-mono font-medium">{snapshotId}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-0.5">Fingerprint</p>
            <p className="text-[13px] font-mono text-muted-foreground">{fingerprint.slice(0, 16)}</p>
          </div>
        </div>
        <p className="text-[12px] text-muted-foreground/50 mt-2">
          This record is now immutable and fully auditable.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={onClose}
          className="h-10 px-6 rounded-xl text-[13px] font-medium bg-foreground text-background hover:opacity-90 transition-all duration-200 flex items-center gap-2 shadow-sm"
        >
          <Eye className="h-4 w-4" /> View Snapshot
        </button>
        <button className="h-10 px-5 rounded-xl text-[13px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-200 flex items-center gap-2">
          <Download className="h-4 w-4" /> Download Report
        </button>
        <button className="h-10 px-5 rounded-xl text-[13px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-200 flex items-center gap-2">
          <Share2 className="h-4 w-4" /> Share
        </button>
      </div>
    </div>
  );
}

// ── Helpers ──
function HealthDot({ label, value, suffix = "" }: { label: string; value: number; suffix?: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className={cn(
        "h-2 w-2 rounded-full",
        value >= 80 ? "bg-success/60" : value >= 50 ? "bg-warning/60" : "bg-destructive"
      )} />
      <span className="text-[13px] text-muted-foreground">{label}</span>
      <span className="text-[13px] font-semibold">{value}{suffix}</span>
    </div>
  );
}
